<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <!-- Header -->
    <header class="relative">
      <!-- Navigation -->
      <nav class="absolute top-0 left-0 right-0 z-50">
        <div class="container mx-auto px-6 py-4">
          <div class="flex items-center justify-between">
            <!-- Logo -->
            <router-link to="/" class="flex items-center space-x-3 group">
              <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-12 h-12 rounded-2xl shadow-lg shadow-emerald-500/25" />
              <span class="text-2xl font-bold text-white">OilBuddyAI</span>
            </router-link>

            <!-- Navigation Links -->
            <div class="hidden md:flex items-center space-x-8">
              <router-link to="/" class="text-slate-300 hover:text-white transition-colors duration-200">Home</router-link>
              <router-link to="/blog" class="text-slate-300 hover:text-white transition-colors duration-200">Blog</router-link>
              <router-link to="/support" class="text-slate-300 hover:text-white transition-colors duration-200">Support</router-link>
              <router-link to="/contact" class="text-emerald-400 font-medium">Contact</router-link>
              <a href="/#/login" class="btn-outline">Sign In</a>
              <router-link to="/register" class="btn-primary">Get Started</router-link>
            </div>

            <!-- Mobile Menu Button -->
            <button @click="toggleMobileMenu" class="md:hidden text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
          </div>

          <!-- Mobile Menu -->
          <div v-if="showMobileMenu" class="md:hidden mt-4 py-4 border-t border-slate-700">
            <div class="flex flex-col space-y-4">
              <router-link to="/" class="text-slate-300 hover:text-white transition-colors duration-200">Home</router-link>
              <router-link to="/blog" class="text-slate-300 hover:text-white transition-colors duration-200">Blog</router-link>
              <router-link to="/support" class="text-slate-300 hover:text-white transition-colors duration-200">Support</router-link>
              <router-link to="/contact" class="text-emerald-400 font-medium">Contact</router-link>
              <a href="/#/login" class="btn-outline">Sign In</a>
              <router-link to="/register" class="btn-primary">Get Started</router-link>
            </div>
          </div>
        </div>
      </nav>
    </header>

    <!-- Main Content -->
    <main class="pt-24 pb-16">
      <div class="container mx-auto px-6">
        <!-- Page Header -->
        <div class="text-center mb-16">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            Get In Touch
          </div>
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Contact Us
          </h1>
          <p class="text-xl text-slate-300 max-w-2xl mx-auto">
            Have questions about OilBuddyAI? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
          </p>
        </div>

        <!-- Contact Information Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <!-- Contact Form -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
            <h2 class="text-2xl font-bold text-white mb-6">Send us a Message</h2>
            <form @submit.prevent="submitForm" class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">First Name</label>
                  <input 
                    v-model="form.firstName"
                    type="text" 
                    required
                    class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                    placeholder="Your first name"
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
                  <input 
                    v-model="form.lastName"
                    type="text" 
                    required
                    class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                    placeholder="Your last name"
                  >
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
                <input 
                  v-model="form.email"
                  type="email" 
                  required
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                  placeholder="your.email@example.com"
                >
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Company (Optional)</label>
                <input 
                  v-model="form.company"
                  type="text" 
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                  placeholder="Your company name"
                >
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Subject</label>
                <select 
                  v-model="form.subject"
                  required
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                >
                  <option value="">Select a subject</option>
                  <option value="general">General Inquiry</option>
                  <option value="sales">Sales Question</option>
                  <option value="support">Technical Support</option>
                  <option value="partnership">Partnership Opportunity</option>
                  <option value="feedback">Feedback</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Message</label>
                <textarea 
                  v-model="form.message"
                  rows="6"
                  required
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 resize-none"
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>

              <button 
                type="submit" 
                :disabled="isSubmitting"
                class="w-full btn-primary-modern disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isSubmitting ? 'Sending...' : 'Send Message' }}
              </button>
            </form>
          </div>

          <!-- Contact Information -->
          <div class="space-y-8">
            <!-- General Contact -->
            <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
              <h2 class="text-2xl font-bold text-white mb-6">Get in Touch</h2>
              <div class="space-y-6">
                <div class="flex items-start space-x-4">
                  <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white mb-1">Email Us</h3>
                    <p class="text-slate-400 mb-2">We'll respond within 24 hours</p>
                    <a href="mailto:contact@oilbuddy.ai" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200">contact@oilbuddy.ai</a>
                  </div>
                </div>

                <div class="flex items-start space-x-4">
                  <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white mb-1">Call Us</h3>
                    <p class="text-slate-400 mb-2">Available Monday-Friday, 9AM-6PM EST</p>
                    <a href="tel:+1-800-OILBUDDY" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200">+1 (800) OILBUDDY</a>
                  </div>
                </div>

                <div class="flex items-start space-x-4">
                  <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white mb-1">Visit Us</h3>
                    <p class="text-slate-400 mb-2">Our headquarters</p>
                    <p class="text-slate-300">123 Oilfield Drive<br>Houston, TX 77001<br>United States</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Support Information -->
            <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
              <h2 class="text-2xl font-bold text-white mb-6">Need Help?</h2>
              <div class="space-y-6">
                <div class="flex items-start space-x-4">
                  <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white mb-1">Support Center</h3>
                    <p class="text-slate-400 mb-2">Find answers to common questions</p>
                    <router-link to="/support" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200">Visit Support Center</router-link>
                  </div>
                </div>

                <div class="flex items-start space-x-4">
                  <div class="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white mb-1">Live Chat</h3>
                    <p class="text-slate-400 mb-2">Get instant help from our team</p>
                    <button @click="startLiveChat" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200">Start Chat</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- FAQ Section -->
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
          <h2 class="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
          <div class="space-y-4">
            <div 
              v-for="(faq, index) in faqs" 
              :key="index"
              class="border border-slate-700/50 rounded-lg overflow-hidden"
            >
              <button 
                @click="toggleFAQ(index)"
                class="w-full px-6 py-4 text-left bg-slate-700/30 hover:bg-slate-700/50 transition-colors duration-200 flex items-center justify-between"
              >
                <span class="text-white font-medium">{{ faq.question }}</span>
                <svg 
                  class="w-5 h-5 text-slate-400 transition-transform duration-200"
                  :class="{ 'rotate-180': openFAQs.includes(index) }"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              <div 
                v-show="openFAQs.includes(index)"
                class="px-6 py-4 bg-slate-700/20 border-t border-slate-700/50"
              >
                <p class="text-slate-300">{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-slate-900/50 border-t border-slate-700/50">
      <div class="container mx-auto px-6 py-12">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- Company Info -->
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center space-x-3 mb-4">
              <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-10 h-10 rounded-xl shadow-lg shadow-emerald-500/25" />
              <span class="text-xl font-bold text-white">OilBuddyAI</span>
            </div>
            <p class="text-slate-400 mb-6 max-w-md">
              Revolutionizing the oil and gas industry with AI-powered solutions for contractors, companies, and professionals.
            </p>
            <div class="flex space-x-4">
              <a href="#" class="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="#" class="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          <!-- Quick Links -->
          <div>
            <h3 class="text-lg font-semibold text-white mb-4">Quick Links</h3>
            <ul class="space-y-2">
              <li><router-link to="/" class="text-slate-400 hover:text-white transition-colors duration-200">Home</router-link></li>
              <li><router-link to="/blog" class="text-slate-400 hover:text-white transition-colors duration-200">Blog</router-link></li>
              <li><router-link to="/support" class="text-slate-400 hover:text-white transition-colors duration-200">Support</router-link></li>
              <li><router-link to="/contact" class="text-slate-400 hover:text-white transition-colors duration-200">Contact</router-link></li>
              <li><router-link to="/roadmap" class="text-slate-400 hover:text-white transition-colors duration-200">Roadmap</router-link></li>
              <li><router-link to="/privacy" class="text-slate-400 hover:text-white transition-colors duration-200">Privacy Policy</router-link></li>
              <li><router-link to="/terms" class="text-slate-400 hover:text-white transition-colors duration-200">Terms of Service</router-link></li>
              <li><router-link to="/data-deletion" class="text-slate-400 hover:text-white transition-colors duration-200">Data Deletion</router-link></li>
            </ul>
          </div>

          <!-- Contact Info -->
          <div>
            <h3 class="text-lg font-semibold text-white mb-4">Contact</h3>
            <ul class="space-y-2">
              <li><a href="mailto:contact@oilbuddy.ai" class="text-slate-400 hover:text-white transition-colors duration-200">contact@oilbuddy.ai</a></li>
              <li><a href="tel:+1-800-OILBUDDY" class="text-slate-400 hover:text-white transition-colors duration-200">+1 (800) OILBUDDY</a></li>
              <li class="text-slate-400">Houston, TX</li>
            </ul>
          </div>
        </div>

        <div class="border-t border-slate-700/50 mt-8 pt-8 text-center">
          <p class="text-slate-400">&copy; 2024 OilBuddyAI. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'

// Mobile menu state
const showMobileMenu = ref(false)

// Form state
const isSubmitting = ref(false)
const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  subject: '',
  message: ''
})

// FAQ state
const openFAQs = ref<number[]>([])

// FAQ data
const faqs = [
  {
    question: "How does OilBuddyAI help oilfield contractors?",
    answer: "OilBuddyAI provides comprehensive AI-powered tools for job management, financial tracking, safety compliance, and business optimization specifically designed for the oil and gas industry."
  },
  {
    question: "What features are included in the free plan?",
    answer: "Our free plan includes basic job tracking, limited AI assistance, and access to the job marketplace. Premium features require a paid subscription."
  },
  {
    question: "Is my data secure on OilBuddyAI?",
    answer: "Yes, we use enterprise-grade security measures including encryption, secure data centers, and compliance with industry standards to protect your sensitive information."
  },
  {
    question: "Can I integrate OilBuddyAI with my existing systems?",
    answer: "Yes, we offer API integrations and can work with your existing accounting, payroll, and project management systems."
  },
  {
    question: "How do I get started with OilBuddyAI?",
    answer: "Simply sign up for a free account, complete your profile, and start exploring our features. Our onboarding process will guide you through setup."
  }
]

// Methods
const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
}

const toggleFAQ = (index: number) => {
  const faqIndex = openFAQs.value.indexOf(index)
  if (faqIndex > -1) {
    openFAQs.value.splice(faqIndex, 1)
  } else {
    openFAQs.value.push(index)
  }
}

const submitForm = async () => {
  isSubmitting.value = true
  
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // Reset form
    Object.assign(form, {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      subject: '',
      message: ''
    })
    
    alert('Thank you for your message! We\'ll get back to you soon.')
  } catch (error) {
    alert('There was an error sending your message. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

const startLiveChat = () => {
  alert('Live chat feature coming soon! For now, please use our contact form or email us directly.')
}
</script>

<style scoped>
.btn-primary-modern {
  @apply inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 transform hover:scale-105;
}

.btn-outline {
  @apply inline-flex items-center px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700/50 hover:text-white transition-colors duration-200;
}

.btn-primary {
  @apply inline-flex items-center px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200;
}
</style>
